import React, { useState } from "react";
import {
  Button,
  Grid,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { DateFilterType } from "../../components/appContext/app-model";
import { useAppContext } from "../../components/appContext/app-provider";
import moment from "moment";

interface DateOption {
  descricao: string;
  tipo: DateFilterType;
  date: Date;
  dateEnd?: Date;
}

const getComparableDate = (dateOption: DateOption): Date => {
  if (dateOption.tipo === DateFilterType.diario) {
    return moment(dateOption.date).add(-1, "d").toDate();
  }
  if (dateOption.tipo === DateFilterType.semanal) {
    return moment(dateOption.date).subtract(1, "w").toDate();
  }
  return moment(dateOption.date).add(-1, "M").toDate();
};

export const getComparePeriodoLabel = (start: Date, end: Date) =>
  `${moment(start).format("DD/MM/YY")} - ${moment(end).format("DD/MM/YY")}`;

export const getComparableDateStr = (date: Date, tipo?: DateFilterType) => {
  const dateMoment = moment(date).startOf("d");
  const finalDeSemana = dateMoment.day() === 0 || dateMoment.day() === 6;
  const nomeRelativoUltimaSemana = finalDeSemana ? "Último" : "Última";

  if (
    tipo === DateFilterType.mensal &&
    dateMoment.diff(moment(new Date()), "d") === 0
  )
    return "Este mês";

  if (tipo === DateFilterType.semanal) {
    const diffWeeks = moment()
      .startOf("isoWeek")
      .diff(dateMoment.clone().startOf("isoWeek"), "w");
    if (diffWeeks === 0) return "Esta semana";
    if (diffWeeks === 1) return "Semana passada";
    return dateMoment.format("[Semana de] DD/MM");
  }

  if (tipo === DateFilterType.periodo) {
    return dateMoment.format("DD/MM/YY");
  }

  return moment(dateMoment).calendar(null, {
    lastDay: "[Ontem]",
    sameDay: "[Hoje]",
    nextDay: "[Amanhã]",
    lastWeek: "[" + nomeRelativoUltimaSemana + "] dddd",
    nextWeek: "dddd",
    sameElse: "MMMM",
  });
};

export interface DateFilterProps {
  dataAtualizacao?: Date;
  extraFilter?: React.ReactNode;
}

export const DateFilter = (props: DateFilterProps) => {
  const hoje = new Date();

  const filterOptions: DateOption[] = [
    { descricao: "Hoje", tipo: DateFilterType.diario, date: hoje },
    {
      descricao: "Ontem",
      tipo: DateFilterType.diario,
      date: moment(hoje).add(-1, "d").toDate(),
    },
    // {
    //   descricao: "Esta semana",
    //   tipo: DateFilterType.semanal,
    //   date: moment(hoje).startOf("isoWeek").toDate(),
    // },
    // {
    //   descricao: "Semana passada",
    //   tipo: DateFilterType.semanal,
    //   date: moment(hoje).subtract(1, "w").startOf("isoWeek").toDate(),
    // },
    { descricao: "Este mês", tipo: DateFilterType.mensal, date: hoje },
    {
      descricao: "Mês passado",
      tipo: DateFilterType.mensal,
      date: moment(hoje).add(-1, "M").toDate(),
    },
    { descricao: "Período...", tipo: DateFilterType.periodo, date: hoje },
  ];

  const appContext = useAppContext();
  const [anchorDateMenu, setanchorDateMenu] = useState<null | HTMLElement>(
    null,
  );
  const [selectedDateFilter, setSelectedDateFilter] = useState<DateOption>(
    filterOptions[0],
  );
  const [periodDialogOpen, setPeriodDialogOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState<Date>(hoje);
  const [periodEnd, setPeriodEnd] = useState<Date>(hoje);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [comparePeriodStart, setComparePeriodStart] = useState<Date>(hoje);
  const [comparePeriodEnd, setComparePeriodEnd] = useState<Date>(hoje);

  const handleDateMenuButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setanchorDateMenu(event.currentTarget);
  };

  const applyFilter = (dateOption: DateOption) => {
    setSelectedDateFilter(dateOption);
    appContext.actions.setFilterDates({
      dateFilterType: dateOption.tipo,
      dateFilter: dateOption.date,
      dateFilterEnd: dateOption.dateEnd,
      comparableDateFilter: getComparableDate(dateOption),
      comparableDateFilterEnd: undefined,
    });
  };

  const handleOptionSelect = (dateOption: DateOption) => {
    setanchorDateMenu(null);
    if (dateOption.tipo === DateFilterType.periodo) {
      setPeriodDialogOpen(true);
      return;
    }
    applyFilter(dateOption);
  };

  const handlePeriodConfirm = () => {
    setPeriodDialogOpen(false);
    const periodOption: DateOption = {
      descricao: `${moment(periodStart).format("DD/MM")} - ${moment(periodEnd).format("DD/MM")}`,
      tipo: DateFilterType.periodo,
      date: periodStart,
      dateEnd: periodEnd,
    };
    applyFilter(periodOption);
  };

  const handleCompareButtonClick = () => {
    const filtroModel = appContext.model.filtroModel;
    setComparePeriodStart(
      filtroModel?.comparableDateFilterEnd
        ? filtroModel.comparableDateFilter!
        : moment(selectedDateFilter.date).subtract(1, "M").toDate(),
    );
    setComparePeriodEnd(
      filtroModel?.comparableDateFilterEnd ??
        moment(selectedDateFilter.dateEnd ?? selectedDateFilter.date)
          .subtract(1, "M")
          .toDate(),
    );
    setCompareDialogOpen(true);
  };

  const handleCompareConfirm = () => {
    setCompareDialogOpen(false);
    appContext.actions.setFilterDates({
      ...appContext.model.filtroModel,
      comparableDateFilter: comparePeriodStart,
      comparableDateFilterEnd: comparePeriodEnd,
    });
  };

  const handleClearComparison = () => {
    setCompareDialogOpen(false);
    appContext.actions.setFilterDates({
      ...appContext.model.filtroModel,
      comparableDateFilter: getComparableDate(selectedDateFilter),
      comparableDateFilterEnd: undefined,
    });
  };

  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: "6px 12px", gap: "8px", flexWrap: "wrap" }}
      >
        <Button
          aria-controls="simple-menu"
          aria-haspopup="true"
          onClick={handleDateMenuButtonClick}
          variant="contained"
        >
          {selectedDateFilter.descricao}
        </Button>
        <Menu
          id="simple-menu"
          anchorEl={anchorDateMenu}
          keepMounted
          open={Boolean(anchorDateMenu)}
          onClose={() => setanchorDateMenu(null)}
        >
          {filterOptions.map((fo) => (
            <MenuItem
              key={"item_" + fo.descricao}
              onClick={() => handleOptionSelect(fo)}
            >
              {fo.descricao}
            </MenuItem>
          ))}
        </Menu>
        {selectedDateFilter.tipo === DateFilterType.periodo && (
          <Button
            aria-label="Comparar com período"
            onClick={handleCompareButtonClick}
            variant={
              appContext.model.filtroModel?.comparableDateFilterEnd
                ? "contained"
                : "outlined"
            }
            size="small"
          >
            {appContext.model.filtroModel?.comparableDateFilterEnd
              ? `Comp: ${getComparePeriodoLabel(
                  appContext.model.filtroModel.comparableDateFilter!,
                  appContext.model.filtroModel.comparableDateFilterEnd,
                )}`
              : "Comparar com..."}
          </Button>
        )}
        {props.extraFilter}
        <span style={{ paddingRight: "6px", paddingLeft: "12px" }}>
          Última atualização:{" "}
          {props.dataAtualizacao
            ? moment(props.dataAtualizacao).calendar()
            : ""}
        </span>
      </Grid>

      <Dialog
        open={periodDialogOpen}
        onClose={() => setPeriodDialogOpen(false)}
      >
        <DialogTitle>Selecionar Período</DialogTitle>
        <DialogContent>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="DD/MM/YYYY"
                label="Data início"
                value={periodStart}
                onChange={(date) => date && setPeriodStart(date.toDate())}
                autoOk
              />
            </Grid>
            <Grid item>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="DD/MM/YYYY"
                label="Data fim"
                value={periodEnd}
                minDate={periodStart}
                onChange={(date) => date && setPeriodEnd(date.toDate())}
                autoOk
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeriodDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handlePeriodConfirm}
            color="primary"
            variant="contained"
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={compareDialogOpen}
        onClose={() => setCompareDialogOpen(false)}
      >
        <DialogTitle>Comparar com período</DialogTitle>
        <DialogContent>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="DD/MM/YYYY"
                label="Data início"
                value={comparePeriodStart}
                onChange={(date) =>
                  date && setComparePeriodStart(date.toDate())
                }
                autoOk
              />
            </Grid>
            <Grid item>
              <KeyboardDatePicker
                disableToolbar
                variant="inline"
                format="DD/MM/YYYY"
                label="Data fim"
                value={comparePeriodEnd}
                minDate={comparePeriodStart}
                onChange={(date) => date && setComparePeriodEnd(date.toDate())}
                autoOk
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearComparison}>Limpar</Button>
          <Button onClick={() => setCompareDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleCompareConfirm}
            color="primary"
            variant="contained"
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
